import { defineStore } from 'pinia'

import RetailApiV2 from '../api/retailApi/RetailApiV2'

interface ICategory {
  name: string
  text: string
  title: string
  permalink: string
  kind: string
  description: string
  image: string
  iconUrl: string
  subcategories?: ICategory[]
}

interface ICategoryV2 {
  name: string
  description: string
  title: string
  permalink: string
  image: string
  subcategories: ICategoryV2[]
  systemName: string
}

type BackendCategory = Omit<ICategory, "kind"> & { systemName: string };

interface ICategories {
  productCategories: ICategory[]
  mixCategories: ICategory[]
  productsCategories: ICategoryV2[]
  eventCategories: ICategoryV2[]
}

export const useCategoriesStore = defineStore('categoriesStore', {
  state: (): ICategories => ({
    productCategories: [],
    mixCategories: [],
    productsCategories: [],
    eventCategories: []
  }),

  actions: {
    loadCategories() {
      this.syncProductCategories()
      this.syncMixCategories()
      this.syncProductsCategories()
    },
    async syncProductCategories() {
      const { data } = await RetailApiV2.get(`products_categories`)
      this.productCategories.push(
        ...data.categories.map((category: BackendCategory) => {
          return {
            name: 'productsPath',
            text: category.title,
            title: category.title,
            permalink: category.permalink,
            kind: category.systemName,
            description: '',
            image: category.image,
            iconUrl: category.iconUrl,
            subcategories: category.subcategories || [],
          }
        }),
      )
    },
    async syncProductsCategories() {
      const { data } = await RetailApiV2.get(`products_categories`)
      this.eventCategories.push(
        ...data.eventCategories.map((category: BackendCategory) => {
          return {
            name: 'productsPath',
            description: category.description,
            title: category.title,
            permalink: category.permalink, 
            image: category.image,
            systemName: category.systemName,
          }
        }),
      )
      this.productsCategories.push(
        ...data.categories.map((category: BackendCategory) => {
          return {
            name: 'productsPath',
            description: category.description,
            title: category.title,
            permalink: category.permalink,
            image: category.image,
            subcategories: category.subcategories,
            systemName: category.systemName
          }
        }),
      )
    },
    async syncMixCategories() {
      const { data } = await RetailApiV2.get(`mix_categories`)
      this.mixCategories.push(
        ...data.categories.map((category: BackendCategory) => {
          return {
            name: 'mixesPath',
            text: category.title,
            title: category.title,
            permalink: category.permalink,
            kind: category.systemName,
            description: '',
            image: category.image,
            iconUrl: category.iconUrl,
          }
        }),
      )
    },
    dynamicProductPath(kind: string) {
      const category = this.productCategories?.find((category) => category.kind === kind)
      return category ? { name: category.name, params: { permalink: category.permalink } } : { name: 'rootPath' }
    },
    dynamicMixPath(permalink: string) {
      const category = this.mixCategories?.find((category) => category.permalink === permalink)
      return category ? { name: category.name, params: { permalink: category.permalink } } : { name: 'rootPath' }
    }
  },

  getters: {
    getProductCategories: (state) => state.productCategories,
    getProductsCategories: (state) => state.productsCategories,
    getEventCategories: (state) => state.eventCategories,
    getMixCategories: (state) => state.mixCategories,
    getMixCategoriesByKind: (state) => (kind: string) => state.mixCategories?.find((category) => category.kind === kind),
    getProductCategoriesByKind: (state) => (kind: string) =>
      state.productCategories?.find((category) => category.kind === kind),
    getMixCategoriesByPermalink: (state) => (permalink: string) =>
      state.mixCategories?.find((category) => category.permalink === permalink),
  },
})
